import { useEffect, useMemo, type ReactNode } from "react";
import type { ImageAttachment } from "./image-preview-strip";
import type { ScreenRecordingsContext } from "@/lib/screen-recordings-context";
import {
  isInputBarQuestionReplyPending,
  inputBarQuestionFor,
  replyToInputBarQuestion,
} from "@/lib/answer-in-input-bar";
import { useCanvasStore } from "@/stores/canvas-store";
import { useInputBarQuestionStore } from "@/stores/input-bar-question-store";
import { ConversationInlineQuestionCard } from "@/components/shapes/conversation-frame/conversation-inline-question-card";
import { isSubagentTarget } from "@/lib/input-bar-target-kind";
import {
  guideSendHoldHint,
  holdSendForGuide,
  useGuideHoldsSends,
  useGuideSendHoldHintVisible,
} from "@/desktop/onboarding/quick-guide/guide-send-hold";
import { usePromptDraft } from "@/hooks/use-prompt-draft";
import { useInputBarTarget } from "@/stores/input-bar-target-store";
import { InputBar } from "./input-bar";
import type { InputBarProps, InputBarWorkerControls } from "./input-bar-types";
import { StandaloneBarPredictionContext } from "./standalone-bar-prediction-scope";
import {
  TASK_CHAT_INPUT_BAR_PROPS,
  useWorkerComposer,
} from "./use-worker-composer";
import { useT } from "@/i18n";
import { inputBarMessages } from "@/i18n/messages/input-bar";
import { WorkerComposerAlerts } from "./worker-composer-alerts";

/** Until a worker snapshot exists (loading, failed, none), the ring still opens
 *  Recent and New chat. */
const NO_WORKER_CONTROLS: InputBarWorkerControls = {
  modelLabel: null,
  contextUsage: null,
  queuedCount: 0,
  control: null,
};

/** Worker controls and the leading notice come from the target, never the caller. */
type InputBarTargetComposerProps = Omit<
  InputBarProps,
  "workerControls" | "leadingNotice"
> & {
  /** One id per bar, kept across target switches, so speech recorded on one
   *  target lands in the draft after switching to another. */
  dictationId: string;
  /** Scripted hosts (the onboarding demo) never show a next-prompt ghost. */
  suppressPrediction?: boolean;
  annotationNotice?: ReactNode;
  prepareQuestionReply?: () => Promise<
    | {
        screenRecordings?: ScreenRecordingsContext;
        images: ImageAttachment[];
        onSent?: () => void;
      }
    | false
  >;
  onQuestionReplySent?: () => void;
};

/** Standalone or main bar; a worker composer while the target is a sub-agent.
 *  Both kinds share one tree, so switching target never remounts the bar. */
export function InputBarTargetComposer({
  dictationId,
  suppressPrediction = false,
  annotationNotice,
  prepareQuestionReply,
  onQuestionReplySent,
  ...props
}: InputBarTargetComposerProps) {
  const t = useT(inputBarMessages);
  const target = useInputBarTarget((state) => state.target);
  // The onboarding guide holds sends; its scripted demo submit is not a send.
  // A held attempt keeps the draft and shows why, so the bar stays quiet
  // until the human actually tries to send.
  const guideHoldsSends = useGuideHoldsSends() && !suppressPrediction;
  const guideHoldHint = useGuideSendHoldHintVisible() && guideHoldsSends;
  const subagent = isSubagentTarget(target) ? target : null;
  const composer = useWorkerComposer({
    enabled: subagent !== null,
    projectId: subagent?.projectId,
    taskId: subagent?.taskId,
    onSubmit: props.onSubmit,
    disabled: props.disabled,
    placeholder: props.placeholder,
  });
  // Question mode (frames redesign §5.4): Answer on a frame's chrome picked
  // this question; it applies to any target.
  const questionId = useInputBarQuestionStore((state) => state.awaitingInputId);
  const questionSnapshot = useInputBarQuestionStore((state) => state.question);
  const awaitingInputs = useCanvasStore((state) => state.awaitingInputs);
  const question = useMemo(
    () =>
      questionId
        ? inputBarQuestionFor(awaitingInputs, questionId, questionSnapshot)
        : null,
    [awaitingInputs, questionId, questionSnapshot],
  );
  useEffect(() => {
    // Answered elsewhere (cursor card, toast) or timed out.
    if (
      questionId &&
      !question &&
      !isInputBarQuestionReplyPending(questionId)
    ) {
      useInputBarQuestionStore.getState().finish();
    }
  }, [question, questionId]);
  // The bar's prompts may open a new conversation for their topic; questions,
  // sub-agents and scripted demos never do.
  usePromptDraft(props.value, !subagent && !question && !suppressPrediction);
  const answerQuestion = async (answer: string, selectedOption?: string) => {
    const attachments = props.images ?? [];
    if (
      !question ||
      (!answer && !attachments.length && !props.hasExtraContent) ||
      attachments.some(
        (attachment) => attachment.uploading || attachment.uploadError,
      )
    )
      return false;
    // Without recordings to prepare, the reply turns pending in this tick.
    const extra = prepareQuestionReply && (await prepareQuestionReply());
    if (extra === false) return false;
    const sent = await replyToInputBarQuestion(
      question.awaiting,
      answer,
      selectedOption,
      [...attachments, ...(extra?.images ?? [])].flatMap((attachment) =>
        attachment.imageId ? [attachment.imageId] : [],
      ),
      extra?.screenRecordings,
    );
    if (sent) {
      attachments.forEach((attachment) => props.onRemoveImage?.(attachment.id));
      extra?.onSent?.();
      onQuestionReplySent?.();
    }
    return sent;
  };
  const answerTyped = () => answerQuestion(props.value.trim());
  const submit = question ? answerTyped : composer.onSubmit;
  const holdForGuide = () => (holdSendForGuide("typed") ? false : submit());
  const questionCard = question ? (
    <ConversationInlineQuestionCard
      key={question.awaiting.id}
      question={{
        awaitingInputId: question.awaiting.id,
        question: question.awaiting.question,
        options: question.awaiting.options,
      }}
      label={
        question.total > 1
          ? t("questionOf", {
              position: question.position,
              total: question.total,
            })
          : t("question")
      }
      testId="input-bar-question"
      className="mb-2"
      onReply={answerQuestion}
    />
  ) : null;
  const workerQuestionCard = composer.question ? (
    <ConversationInlineQuestionCard
      key={composer.question.awaitingInputId}
      question={composer.question}
      testId="input-bar-target-question"
      className="mb-2"
      onAnswered={composer.onQuestionAnswered}
    />
  ) : null;
  // InputBar positions its own root at the cursor anchor, so the card and the
  // alert line render inside it, above the chip.
  const notice =
    annotationNotice ??
    (guideHoldHint ? (
      <div
        className="rounded-preview bg-glass-fill-heavy px-2 py-1 text-appkit-caption text-secondary"
        data-testid="guide-send-hold-notice"
      >
        {guideSendHoldHint()}
      </div>
    ) : null);
  const leadingNotice =
    question || subagent || notice ? (
      <>
        {notice}
        {questionCard ?? workerQuestionCard}
        {subagent ? (
          <WorkerComposerAlerts
            {...composer.alerts}
            testIdPrefix="input-bar-target"
          />
        ) : null}
      </>
    ) : undefined;
  return (
    // The standalone bar's controller reads this scope: no ghost for a scripted
    // host, under a question card, or on a blocked worker bar. A worker bar is
    // blocked until its controls load, so its ghost arrives then and the bar
    // re-measures its width. Transient disables (closing, sending) leave the
    // ghost alone.
    <StandaloneBarPredictionContext.Provider
      value={{
        enabled:
          !suppressPrediction &&
          !composer.blocked &&
          !composer.question &&
          !question,
      }}
    >
      <InputBar
        {...props}
        dictationId={dictationId}
        // A reply never goes through the worker, so its blocking doesn't apply.
        disabled={question ? props.disabled : composer.disabled}
        placeholder={question ? t("answerQuestion") : composer.placeholder}
        onSubmit={guideHoldsSends ? holdForGuide : submit}
        leadingNotice={leadingNotice}
        workerControls={
          subagent
            ? {
                ...(composer.workerControls ?? NO_WORKER_CONTROLS),
                // Read before TASK_CHAT_INPUT_BAR_PROPS nulls it for the pill.
                contextMenuAgent: props.focusedAgent ?? null,
              }
            : undefined
        }
        {...(subagent ? TASK_CHAT_INPUT_BAR_PROPS : {})}
        {...(subagent && composer.workerProvider
          ? {
              allowAttachments: true,
              images: props.images,
              onImageFile: props.onImageFile,
              onRemoveImage: props.onRemoveImage,
            }
          : {})}
        {...(question
          ? {
              textOnly: true,
              allowAttachments: true,
              targetPicker: false,
              images: props.images,
              onImageFile: props.onImageFile,
              isStreaming: false,
              onStop: undefined,
            }
          : {})}
      />
    </StandaloneBarPredictionContext.Provider>
  );
}
